import React, { useEffect, useState, useRef } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import deepEqual from "fast-deep-equal";
import FrameCard from "../../shared/FrameCard";
import EvaluationStandardsSection from "../EvaluationStandardsSection";
import SkillCard from "../SkillCard";
import ActionButton from "../../shared/ActionButton";
import { PROJECT_COLOR, PROJECT_COLOR_DARK } from "../../../constants/colors";
import { toast } from "react-toastify";
import { normalizeForDirty } from "../../utils/normalizeForDirty";
import { useDirtyState } from "../../shared/hooks/useDirtyState";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import {
  targetFromFormData,
  memberSkillSheetPathFor,
} from "../../utils/skillSheetRouting";

export default function SelfEvaluationsEdit() {
  const navigate = useNavigate();
  const [skillCategories, setSkillCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDesktop = useIsDesktop();
  const [target, setTarget] = useState(null);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(false);

  // dirty判定用
  const initialSnapshotRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  // データ取得
  useEffect(() => {
    setError(false);

    // ?company_user_id=8 とか ?user_id=41 を拾う
    const searchParamsString = searchParams.toString();
    const searchString = searchParamsString ? `?${searchParamsString}` : "";

    fetch(`/skill_sheet_self_evaluations/edit.json${searchString}`)
      .then((response) => response.json())
      .then((data) => {
        const categories = data.skillCategories || [];
        setSkillCategories(categories);
        setTarget({
          user_id: data.user_id,
          company_user_id: data.company_user_id,
        });
        setLoading(false);

        // 初期値をスナップショットとして保存（比較用）
        initialSnapshotRef.current = normalizeForDirty(categories);
        setDirty(false);
      })
      .catch((error) => {
        console.error("API error", error);
        setError(true);
        toast.error("読み込みに失敗しました。", { toastId: "fetch-error" });
      });
  }, [searchParams]);

  // skillCategories が更新されたら dirty を再計算する
  useEffect(() => {
    if (loading) return; // 初期ロード中は判定しない

    const initial = initialSnapshotRef.current;
    const current = normalizeForDirty(skillCategories);
    const nextDirty = !deepEqual(initial, current);
    setDirty(nextDirty);
  }, [skillCategories, loading]);

  // dirty が変わったときだけ Layout に通知
  useDirtyState(dirty);

  // 親スキル変更
  function handleParentLevelChange(categoryKey, groupId, newLevelValue) {
    setSkillCategories((previousCategories) =>
      previousCategories.map((category) => {
        if (category.categoryKey !== categoryKey) return category;

        return {
          ...category,
          skillGroups: category.skillGroups.map((group) => {
            if (group.groupId !== groupId) return group;

            return {
              ...group,
              parentSkill: {
                ...group.parentSkill,
                levelValue: newLevelValue,
              },
            };
          }),
        };
      }),
    );
  }

  // 子スキル変更
  function handleChildLevelChange(
    categoryKey,
    groupId,
    recordId,
    newLevelValue,
  ) {
    setSkillCategories((previousCategories) =>
      previousCategories.map((category) => {
        if (category.categoryKey !== categoryKey) return category;

        return {
          ...category,
          skillGroups: category.skillGroups.map((group) => {
            if (group.groupId !== groupId) return group;

            return {
              ...group,
              childSkills: group.childSkills.map((child) => {
                if (child.recordId !== recordId) return child;
                return { ...child, levelValue: newLevelValue };
              }),
            };
          }),
        };
      }),
    );
  }

  if (error) {
    return (
      <Box p={4} ml={4}>
        <Typography>
          読み込みに失敗しました。
          <br />
          通信環境をご確認し、再読み込みしてください。
        </Typography>
      </Box>
    );
  }

  // データ未取得時のローディング表示
  if (loading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  function levelBeforeTypeCastFromLevelValue(levelValue) {
    const normalized = Number.isFinite(levelValue) ? levelValue : 25;
    const clamped = Math.min(100, Math.max(0, normalized));
    return Math.round(clamped / 25) + 1; // 0→1, 25→2, ... 100→5
  }

  function buildSkillSheetTemplateAttributesFromSkillCategories(
    skillCategories,
  ) {
    const attributes = {
      skill_sheet_language_infos_attributes: {},
      skill_sheet_business_system_infos_attributes: {},
      skill_sheet_cloud_services_attributes: {},
      skill_sheet_server_operating_system_infos_attributes: {},
      skill_sheet_middleware_infos_attributes: {},
      skill_sheet_operating_system_infos_attributes: {},
      skill_sheet_db_infos_attributes: {},
      skill_sheet_network_infos_attributes: {},
      skill_sheet_design_tool_infos_attributes: {},
    };

    function setAttributes(attributeKey, recordId, levelKeyName, levelValue) {
      if (!recordId) return;
      // 既存のデータがある場合はマージし、ない場合は新規作成
      attributes[attributeKey][String(recordId)] = {
        ...attributes[attributeKey][String(recordId)],
        id: recordId,
        [levelKeyName]: levelBeforeTypeCastFromLevelValue(levelValue),
      };
    }

    for (const category of skillCategories) {
      for (const skillGroup of category.skillGroups) {
        const parentSkill = skillGroup.parentSkill;
        const recordId = parentSkill?.recordId;
        const levelValue = parentSkill?.levelValue;

        switch (category.categoryKey) {
          case "front_end":
          case "back_end": {
            if (recordId) {
              upsertLanguageInfoAttributes(attributes, recordId, {
                language_level: levelBeforeTypeCastFromLevelValue(levelValue),
              });
            }
            for (const childSkill of skillGroup.childSkills || []) {
              if (!childSkill?.recordId) continue;
              upsertLanguageInfoAttributes(attributes, childSkill.recordId, {
                fw_library_level: levelBeforeTypeCastFromLevelValue(
                  childSkill.levelValue,
                ),
              });
            }
            break;
          }

          case "business_system": {
            // 親のレベル設定
            setAttributes(
              "skill_sheet_business_system_infos_attributes",
              recordId,
              "business_system_level",
              levelValue,
            );
            // 子のレベル設定
            for (const child of skillGroup.childSkills || []) {
              setAttributes(
                "skill_sheet_business_system_infos_attributes",
                child.recordId,
                "business_system_product_language_level",
                child.levelValue,
              );
            }
            break;
          }

          case "cloud": {
            setAttributes(
              "skill_sheet_cloud_services_attributes",
              recordId,
              "cloud_service_level",
              levelValue,
            );
            for (const child of skillGroup.childSkills || []) {
              setAttributes(
                "skill_sheet_cloud_services_attributes",
                child.recordId,
                "cloud_service_product_level",
                child.levelValue,
              );
            }
            break;
          }

          case "server_operating_system": {
            setAttributes(
              "skill_sheet_server_operating_system_infos_attributes",
              recordId,
              "server_operating_system_level",
              levelValue,
            );
            for (const child of skillGroup.childSkills || []) {
              setAttributes(
                "skill_sheet_server_operating_system_infos_attributes",
                child.recordId,
                "server_environment_level",
                child.levelValue,
              );
            }
            break;
          }

          case "middleware": {
            setAttributes(
              "skill_sheet_middleware_infos_attributes",
              recordId,
              "level",
              levelValue,
            );
            for (const child of skillGroup.childSkills || []) {
              setAttributes(
                "skill_sheet_middleware_infos_attributes",
                child.recordId,
                "middleware_product_level",
                child.levelValue,
              );
            }
            break;
          }

          case "operating_system":
          case "db":
          case "network":
          case "design_tool":
            setAttributes(
              `skill_sheet_${category.categoryKey}_infos_attributes`,
              recordId,
              "level",
              levelValue,
            );
            break;

          default:
            break;
        }
      }
    }

    return attributes;
  }

  function upsertLanguageInfoAttributes(attributes, recordId, partial) {
    if (!recordId) return;

    const key = String(recordId);

    attributes.skill_sheet_language_infos_attributes[key] = {
      ...(attributes.skill_sheet_language_infos_attributes[key] || {}),
      id: recordId,
      ...partial,
    };
  }

  // 保存
  async function handleSave() {
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const skillSheetTemplateAttributes =
        buildSkillSheetTemplateAttributesFromSkillCategories(skillCategories);

      const response = await fetch("/skill_sheet_self_evaluations", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({
          skill_sheet_template: skillSheetTemplateAttributes,
          user_id: target?.user_id,
          company_user_id: target?.company_user_id,
        }),
        credentials: "same-origin",
      });

      if (!response.ok) {
        const responseBody = await response.json().catch(() => null);

        if (responseBody?.errors?.length) {
          responseBody.errors.forEach((message) => toast.error(message));
        } else {
          toast.error(`保存に失敗しました（${response.status}）`);
        }

        console.error("save failed", response.status, responseBody);
        return;
      }

      toast.success("自己評価を更新しました。");

      // 保存成功したので「保存済みスナップショット」を更新して dirty を解除
      initialSnapshotRef.current = normalizeForDirty(skillCategories);
      setDirty(false);

      // 保存後の遷移先
      const owner = targetFromFormData(target);
      navigate(memberSkillSheetPathFor(owner));
    } catch (error) {
      console.error("save error", error);
      toast.error("通信エラーで保存に失敗しました");
    }
  }

  // カラムの比率
  const categoryEvaluationGridColumns = isDesktop
    ? "minmax(200px, 0.35fr) minmax(260px, 0.65fr)"
    : "1fr";

  return (
    <Box onSubmitCapture={(event) => event.preventDefault()}>
      <FrameCard sx={{ p: isDesktop ? 8 : 2 }}>
        <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 4 }}>
          自己評価編集
        </Typography>

        <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: "8px" }}>
          評価基準
        </Typography>
        <EvaluationStandardsSection isDesktop={isDesktop} />

        {!isDesktop ? (
          <Box
            sx={{
              mt: 7,
              mb: 2,
              py: "12px",
              bgcolor: "#E9EAFA",
            }}
          >
            <Typography sx={{ display: "grid", fontSize: "12px" }}>
              自己評価
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: categoryEvaluationGridColumns,
              mt: 7,
              mb: 4,
              py: "12px",
              bgcolor: "#E9EAFA",
            }}
          >
            <Typography sx={{ display: "grid", pl: 4, fontSize: "12px" }}>
              カテゴリー
            </Typography>
            <Typography sx={{ display: "grid", fontSize: "12px" }}>
              自己評価
            </Typography>
          </Box>
        )}

        {/* ツール以外を表示 */}
        {skillCategories
          .filter((category) => category.categoryLabel !== "ツール")
          .map((category, categoryIndex) => (
            <React.Fragment key={category.categoryKey}>
              {categoryIndex !== 0 && <Divider sx={{ my: 4 }} />}
              <Box
                key={category.categoryLabel}
                sx={{
                  display: "grid",
                  gridTemplateColumns: categoryEvaluationGridColumns,
                  columnGap: isDesktop ? 3 : 0,
                  rowGap: isDesktop ? 0 : 2,
                  alignItems: isDesktop ? "center" : "stretch",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isDesktop ? "center" : "flex-start",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold" }}>
                    【{category.categoryLabel}】
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {category.skillGroups.map((group) => (
                    <SkillCard
                      key={group.groupId}
                      parentSkill={group.parentSkill}
                      childSkills={group.childSkills}
                      onParentLevelChange={(value) =>
                        handleParentLevelChange(
                          category.categoryKey,
                          group.groupId,
                          value,
                        )
                      }
                      onChildLevelChange={(recordId, value) =>
                        handleChildLevelChange(
                          category.categoryKey,
                          group.groupId,
                          recordId,
                          value,
                        )
                      }
                    />
                  ))}
                </Box>
              </Box>
            </React.Fragment>
          ))}
        <Box
          sx={{
            mt: isDesktop ? 10 : 5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ActionButton
            label="保存する"
            sx={{
              backgroundColor: PROJECT_COLOR,
              "&:hover": { backgroundColor: PROJECT_COLOR_DARK },
            }}
            onClick={(event) => {
              event?.preventDefault?.();
              event?.stopPropagation?.();
              handleSave();
            }}
          />
        </Box>
      </FrameCard>
    </Box>
  );
}
